import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useTransactionDetail } from '@/hooks/useTransactionDetail';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useFetchAccounts } from '@accountHooks';
import { CurrencyToggle } from '@/components/currency-toggle/CurrencyToggle';
import { TransactionDetailField } from '@/pages/transactions/components/TransactionDetailField';
import { AccountInfoTooltip } from '@/pages/transactions/components/AccountInfoTooltip';
import { captureSentryException } from '@/integrations/sentry/client';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export default function TransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: transaction, isLoading, error } = useTransactionDetail(id!);
  const { data: accounts } = useFetchAccounts();

  const availableCurrencies = useMemo(() => {
    if (!accounts || accounts.length === 0) return ['USD'];
    const currencies = [...new Set(accounts.map((a) => a.currency))];
    return currencies.length > 0 ? currencies : ['USD'];
  }, [accounts]);

  const nativeCurrency = transaction?.currency ?? 'USD';
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(undefined);
  const displayCurrency = selectedCurrency ?? nativeCurrency;

  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates(nativeCurrency);

  const displayedAmount = useMemo(() => {
    if (!transaction) return null;
    if (ratesLoading || ratesError) return transaction.amount;
    if (displayCurrency === nativeCurrency) return transaction.amount;
    try {
      return convert(transaction.amount, nativeCurrency, displayCurrency);
    } catch {
      return transaction.amount;
    }
  }, [transaction, displayCurrency, nativeCurrency, convert, ratesLoading, ratesError]);

  function handleBack() {
    if (window.history.length <= 1) {
      navigate('/transactions');
    } else {
      navigate(-1);
    }
  }

  // Report error to Sentry
  if (error) {
    captureSentryException(error as Error);
  }

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error && (error as Error).message !== 'Transaction not found') {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-lg font-medium">Failed to load transaction</p>
            <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Not-found state ───────────────────────────────────────────────────────
  if (!transaction || (error && (error as Error).message === 'Transaction not found')) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <Receipt className="h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Transaction not found</p>
            <p className="text-sm text-muted-foreground">
              The transaction you're looking for doesn't exist or you don't have access to it.
            </p>
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Loaded state ──────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Go back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold truncate">Transaction Detail</h1>
          <p className="text-sm text-muted-foreground">{transaction.id}</p>
        </div>
        <CurrencyToggle
          baseCurrency={nativeCurrency}
          availableCurrencies={availableCurrencies}
          selectedCurrency={displayCurrency}
          onCurrencyChange={setSelectedCurrency}
          showBalance={false}
        />
      </div>

      {/* Exchange rate unavailable alert */}
      {ratesError && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Exchange rate unavailable. Showing amount in native currency ({nativeCurrency}).
          </AlertDescription>
        </Alert>
      )}

      {/* Transaction Details */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Details</CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionDetailField
            label="Amount"
            value={formatCurrency(displayedAmount!, displayCurrency)}
          />
          <TransactionDetailField label="Currency" value={transaction.currency} />
          <TransactionDetailField label="Type" value={transaction.type} />
          <TransactionDetailField
            label="Date"
            value={formatDate(transaction.transaction_date)}
          />
          <TransactionDetailField
            label="Account"
            value={
              transaction.accounts ? (
                <AccountInfoTooltip
                  accountId={transaction.accounts.id}
                  accountName={transaction.accounts.name}
                  navigateTo={`/accounts/${transaction.accounts.id}`}
                />
              ) : null
            }
          />
          <TransactionDetailField
            label="Category"
            value={transaction.categories?.name ?? null}
          />
          <TransactionDetailField
            label="Sub-category"
            value={transaction.sub_categories?.name ?? null}
          />
          <TransactionDetailField
            label="Description"
            value={transaction.description ?? null}
          />
          <TransactionDetailField
            label="Notes"
            value={transaction.notes ?? null}
          />
          <TransactionDetailField
            label="Fee"
            value={transaction.fee && transaction.fee > 0
              ? formatCurrency(transaction.fee, transaction.currency)
              : null}
          />
          <TransactionDetailField
            label="Transaction Subtype"
            value={transaction.transaction_subtype ?? null}
          />
          <TransactionDetailField
            label="Transfer ID"
            value={transaction.transfer_id ?? null}
          />
          <TransactionDetailField
            label="Linked Transaction"
            value={transaction.linked_transaction_id ? (
              <Link
                to={`/transactions/${transaction.linked_transaction_id}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {transaction.linked_transaction_id}
              </Link>
            ) : null}
          />
        </CardContent>
      </Card>
    </div>
  );
}
